/**
 * 카드뉴스 뷰어 — **낱장 사진 갤러리가 아니라 한 편의 카드묶음**을 읽는 화면.
 *
 *   $j.Ui.CardNews.open({ items: [{url}, …], index: 0, title: '안내' });
 *
 * ── 왜 ZoomPhoto 를 쓰지 않나 ────────────────────────────────────────
 * `$j.Ui.ZoomPhoto` 는 **화면에 있는 사진을 전부 모아** 넘기는 갤러리다(불러톡에서는
 * `img[id^=chatImage_]` 전부). 그래서 카드 한 장을 열면 대화창의 다른 사진들과 뒤섞여,
 * '이 카드묶음'이라는 단위가 사라진다. 카드뉴스는 **주어진 묶음만** 처음부터 끝까지 읽는
 * 것이 전부라, 모으는 규칙도 읽는 방식도 다르다 — 그래서 별도 컴포넌트다.
 *
 * ── 세로로 읽는다 ──────────────────────────────────────────────────
 * 카드를 **위에서 아래로 이어 읽는다**(스토리처럼 한 장씩 넘기지 않는다). 넘기는 손짓을
 * 따로 배워야 하면 두 번째 장부터는 아무도 안 본다. 그림은 **화면 폭에 맞추고** 길면 길게
 * 흘린다(높이를 화면에 가두지 않는다) — 그래야 한 편이 끊기지 않는다.
 *
 * ── 조작을 글로 가르치지 않는다 ────────────────────────────────────
 * '아래로 내려 보세요'·'두 손가락으로 벌려 보세요' 를 상단에 적어 봤지만 눈에 안 들어왔다.
 * 상단 줄은 닫기·카운터가 있는 **시스템 영역**이라 사람은 거기를 안 본다. 그래서 안내를
 * 그림 쪽으로 옮기고, 말 대신 보이는 것으로 바꿨다:
 *
 *   · 더 있다 → 화면에 꼭 맞는 장은 아래를 PEEK 만큼 남겨 **다음 장 윗머리를 보여준다**
 *               + 열 때 한 번 튕기고 + 하단 페이드와 ⌄
 *   · 커진다  → 상단에 한 줄로 적는다. 두 번 탭도 같은 동작으로 받는다
 *
 * ── 확대 ───────────────────────────────────────────────────────────
 * 브라우저 핀치에 기대면 사이트마다 갈린다 — `/m` 은 `user-scalable=yes` 라 되고 **`/ma` 는
 * `user-scalable=no` 라 두 손가락을 벌려도 아무 일이 없다**(madmin/includes/meta). 그래서
 * ZoomPhoto 와 같은 방식으로 직접 받는다.
 *
 *   배율 1  : 세로 스크롤로 읽는다 (휠·손가락 모두 브라우저에 맡긴다)
 *   배율 >1 : **스크롤을 잠그고** 끌기는 그림 이동, 휠은 배율. 세로 한 축을 스크롤과
 *             팬이 나눠 쓰면 어느 쪽인지 알 수 없어서, 아예 한 번에 하나만 하게 한다
 *
 * ⚠ 공용(Ui/) 컴포넌트라 사이트 토큰(--adm-*, --color-*)을 읽지 않는다.
 *   자기 토큰 `--ui-cardnews-*` 만 읽고 기본값은 이 파일이 갖는다 (.ai/frontend.md 「공용 UI 컴포넌트와 사이트 격리」).
 *   스타일 정본은 `resources/sass/shared/components/_card-news.scss`.
 */
const KEY = 'cardnews';

const PEEK        = 44;    // 화면에 꼭 맞는 장 아래로 남길 여백 — 다음 장 윗머리가 보일 만큼
const NEAR_FIT    = 0.93;  // 무대 높이의 이 비율을 넘게 채우면 '꼭 맞는 장'으로 본다
const BOUNCE      = 34;    // 열 때 한 번 내렸다 올리는 거리
const ZOOM_BY_TAP = 2.4;   // 버튼·두 번 탭으로 키우는 배율

export default class CardNews {
    constructor() {
        this.$el = null;
        this.items = [];
        this.index = 0;          // 지금 화면 한가운데 있는 장 (스크롤이 정한다)
        // 확대 상태. last* 는 손(마우스)을 뗀 시점 값 — 다음 제스처의 출발점
        this._zoom = { scale: 1, lastScale: 1, x: 0, y: 0, lastX: 0, lastY: 0 };
    }

    /**
     * @param {object} opts
     * @param {Array<{url:string}>} opts.items 카드 장 목록 (순서 그대로)
     * @param {number} [opts.index] 처음 보여줄 장 (0-base)
     * @param {string} [opts.title] 상단 제목 (없으면 안 그린다)
     */
    open({ items = [], index = 0, title = '' } = {}) {
        this.items = (items || []).filter(i => i && i.url);
        if (!this.items.length) return;

        this.index = Math.min(Math.max(0, index | 0), this.items.length - 1);
        this.close();   // 이미 떠 있으면 갈아끼운다

        const z = $j.Ui.Layer.push(KEY);
        const total = this.items.length;

        this.$el = $(`
<div class="ui-cardnews" role="dialog" aria-modal="true">
    <div class="ui-cardnews__bars" aria-hidden="true">
        ${this.items.map((_, i) => `<span class="ui-cardnews__bar" data-i="${i}"></span>`).join('')}
    </div>
    <div class="ui-cardnews__top">
        <div class="ui-cardnews__lead">
            <span class="ui-cardnews__tip">두 손가락으로 벌려 크게 보세요</span>
            ${title ? `<span class="ui-cardnews__title">${this._esc(title)}</span>` : ''}
        </div>
        <span class="ui-cardnews__count"><b>${this.index + 1}</b> / ${total}</span>
        <button type="button" class="ui-cardnews__close" aria-label="닫기">&times;</button>
    </div>

    <div class="ui-cardnews__stage">
        <div class="ui-cardnews__track">
            ${this.items.map(it => `<div class="ui-cardnews__slide"><img src="${this._esc(it.url)}" alt=""></div>`).join('')}
            ${total > 1 ? `<div class="ui-cardnews__end">
                <button type="button" class="ui-cardnews__again">
                    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
                        <line x1="5" y1="4" x2="19" y2="4"></line>
                        <line x1="12" y1="21" x2="12" y2="9"></line>
                        <polyline points="6 15 12 9 18 15"></polyline>
                    </svg>맨위로
                </button>
                <button type="button" class="ui-cardnews__done">다 봤어요</button>
            </div>` : ''}
        </div>
    </div>

    <div class="ui-cardnews__more" aria-hidden="true">
        <svg class="ui-cardnews__chev" viewBox="0 0 24 24" width="22" height="22"><path d="M5 9l7 7 7-7"></path></svg>
    </div>

</div>`).css('z-index', ($j.Ui.Layer.z(KEY) || {}).panel || (1060 + z * 20));

        $('body').append(this.$el).addClass('ui-cardnews-open');
        this._openIndex = this.index;
        this._userMoved = false;
        this._bounced = false;
        this._bind();
        this.go(this.index, false);   // 넘겨받은 장에서 시작
        this._sync();

        // ⚠ **그림이 실린 뒤 다시 잡아야 한다.** 여는 순간엔 아직 안 실려 장 높이가 0 이고,
        //    그 상태로 잰 자리는 전부 어긋난다(넘겨받은 장으로 못 가고 n/N 도 틀린다).
        //    손을 댄 뒤에는 다시 잡지 않는다 — 읽던 자리를 빼앗는 꼴이 된다.
        //
        //    같은 이유로 **마무리 줄(맨위로·다 봤어요)을 그동안 감춘다.** 장 높이가 0 이면 그
        //    줄이 맨 위로 올라와, 카드가 뜨기 전 몇 초 동안 버튼만 덩그러니 보인다.
        const $imgs = this.$el.find('.ui-cardnews__slide img');
        this._pending = $imgs.filter((i, img) => !img.complete).length;
        $imgs.each((i, img) => {
            if (img.complete) { this._fit(img); return; }
            $(img).one('load error', () => {
                this._fit(img);
                if (!this._userMoved) this.go(this._openIndex, false);
                this._sync();
                this._bounce();
                if (--this._pending <= 0) this._ready();
            });
        });
        this._bounce();
        if (!this._pending) this._ready();
        // 한 장이라도 영영 안 실리는 경우까지 감춰 둘 수는 없다 — 시간이 지나면 그냥 보여준다
        setTimeout(() => this._ready(), 5000);
    }

    close() {
        if (!this.$el) return;
        $(document).off('keydown.uicardnews mousemove.uicardnews mouseup.uicardnews');
        $(window).off('resize.uicardnews');
        this.$el.remove();
        this.$el = null;
        $('body').removeClass('ui-cardnews-open');
        $j.Ui.Layer.remove(KEY);
    }

    /** 다음 장. 마지막에서 더 내리면 **닫는다** — 다 읽었다는 뜻이다. */
    next() {
        if (this.index >= this.items.length - 1) { this.close(); return; }
        this.go(this.index + 1);
    }

    /** 이전 장. 첫 장에서는 아무 일도 없다 (닫으면 실수로 닫는 일이 잦다). */
    prev() {
        if (this.index <= 0) return;
        this.go(this.index - 1);
    }

    /** i 번째 장을 화면 위로 올린다. 스크롤이 움직이면 _sync 가 나머지를 맞춘다. */
    go(i, smooth = true) {
        if (!this.$el || i < 0 || i >= this.items.length) return;
        const stage = this._stage();
        const el = this.$el.find('.ui-cardnews__slide').get(i);
        if (!stage || !el) return;
        const top = stage.scrollTop + el.getBoundingClientRect().top - stage.getBoundingClientRect().top;
        if (smooth && stage.scrollTo) stage.scrollTo({ top, behavior: 'smooth' });
        else stage.scrollTop = top;
    }

    /** 확대 켜기/끄기 — 두 번 탭(과 PC 두 번 클릭)이 쓴다 */
    toggleZoom() {
        this._setZoom(this._zoom.scale > 1 ? 1 : ZOOM_BY_TAP);
        this._commitZoom();
    }

    /* ───────────────── internal ───────────────── */

    _stage() { return this.$el ? this.$el.find('.ui-cardnews__stage')[0] : null; }

    /** 그림이 다 자리를 잡았다 — 마무리 줄을 드러낸다 (CSS: .is-ready) */
    _ready() {
        if (!this.$el) return;
        this._pending = 0;
        this.$el.addClass('is-ready');
    }

    /**
     * 화면에 **꼭 맞는** 장만 조금 줄여 아래를 PEEK 만큼 남긴다.
     *
     * 딱 맞게 채워진 카드는 '이게 전부'로 읽힌다 — 실제로 실측한 카드가 무대 높이의 99% 였다.
     * 짧은 장은 이미 다음 장이 보이고, 긴 장은 잘린 것 자체가 신호라 둘 다 건드리지 않는다.
     */
    _fit(img) {
        const stage = this._stage();
        if (!stage || !img.naturalWidth) return;
        const w = img.parentElement.clientWidth;
        const h = w * img.naturalHeight / img.naturalWidth;
        const room = stage.clientHeight;
        if (h > room * NEAR_FIT && h <= room) {
            img.style.maxHeight = (room - PEEK) + 'px';
            img.style.width = 'auto';
        } else {
            img.style.maxHeight = '';
            img.style.width = '';
        }
    }

    _fitAll() { this.$el && this.$el.find('.ui-cardnews__slide img').each((i, img) => this._fit(img)); }

    /** 열 때 한 번 내렸다 올린다 — 글자보다 움직임이 먼저 눈에 들어온다. 한 번만. */
    _bounce() {
        const stage = this._stage();
        if (!stage || this._bounced || this._userMoved) return;
        if (stage.scrollHeight - stage.clientHeight < BOUNCE) return;   // 더 볼 게 없으면 튕기지 않는다
        this._bounced = true;
        const base = stage.scrollTop;
        const back = () => { if (!this._userMoved && this.$el) stage.scrollTo({ top: base, behavior: 'smooth' }); };
        if (stage.scrollTo) { stage.scrollTo({ top: base + BOUNCE, behavior: 'smooth' }); setTimeout(back, 430); }
    }

    _bind() {
        const self = this;
        const $el = this.$el;

        $el.on('click', '.ui-cardnews__close, .ui-cardnews__done', () => this.close());
        // 끝까지 읽고 나서 앞을 다시 보고 싶을 때 — 위로 한참 밀어 올리는 수밖에 없었다
        $el.on('click', '.ui-cardnews__again', () => { this._userMoved = true; this.go(0); });
        $el.on('click', '.ui-cardnews__bar', function(){
            self._userMoved = true;
            self.go(parseInt($(this).attr('data-i'), 10));
        });
        $el.on('dblclick', '.ui-cardnews__slide img', (e) => { e.preventDefault(); this.toggleZoom(); });

        // 바탕(그림 옆 여백)을 누르면 닫는다
        $el.on('click', (e) => { if (e.target === $el[0]) this.close(); });

        // 손을 댄 순간부터는 자동 재배치(load 보정·튕김)를 멈춘다 — 읽던 자리를 빼앗지 않는다
        $el.on('touchstart wheel mousedown', () => { this._userMoved = true; });

        // 어느 장을 보고 있는지 — 스크롤이 정본이다. 프레임당 한 번만 계산한다.
        // ⚠ 위임(`$el.on('scroll', '.stage')`)으로 걸지 않는다 — **scroll 은 버블링하지 않아서**
        //   조용히 한 번도 안 불린다. 무대에 직접 건다.
        let ticking = false;
        $el.find('.ui-cardnews__stage').on('scroll', () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => { ticking = false; this._sync(); });
        });

        $(window).on('resize.uicardnews', () => { this._fitAll(); this._sync(); });

        // ── 손가락 ──
        //  배율 1 : 아무것도 가로채지 않는다 — 세로 스크롤은 브라우저가 제일 잘한다
        //  두 손가락 : 확대 / 두 번 탭 : 확대 켜고 끄기
        //  배율 >1 : 한 손가락은 그림 이동 (스크롤은 CSS 로 잠가 둔다)
        let pinching = false, panning = false, startDist = 0, grabX = 0, grabY = 0;
        let tapAt = 0, tapX = 0, tapY = 0;

        const dist = (t) => {
            const dx = t[0].clientX - t[1].clientX, dy = t[0].clientY - t[1].clientY;
            return Math.sqrt(dx * dx + dy * dy);
        };

        $el.on('touchstart', (e) => {
            const ts = e.originalEvent.touches;
            if (ts.length === 2) {
                e.preventDefault();
                this._sync();                 // 지금 보고 있는 장에 배율을 건다
                pinching = true; panning = false;
                startDist = dist(ts) || 1;
                return;
            }
            if (ts.length !== 1 || this._zoom.scale <= 1) return;
            panning = true;
            grabX = ts[0].clientX - this._zoom.lastX;
            grabY = ts[0].clientY - this._zoom.lastY;
        });

        $el.on('touchmove', (e) => {
            const ts = e.originalEvent.touches;
            if (pinching && ts.length === 2) {
                e.preventDefault();
                this._setZoom(this._zoom.lastScale * (dist(ts) / startDist));
                return;
            }
            if (panning && ts.length === 1) {
                e.preventDefault();
                this._zoom.x = ts[0].clientX - grabX;
                this._zoom.y = ts[0].clientY - grabY;
                this._applyZoom();
            }
        });

        $el.on('touchend touchcancel', (e) => {
            const oe = e.originalEvent;
            if (pinching || panning) {
                if (oe.touches.length) return;   // 아직 손가락이 남았다
                pinching = panning = false;
                this._commitZoom();
                tapAt = 0;                       // 확대/이동 뒤의 손뗌은 '탭'이 아니다
                return;
            }
            // 두 번 탭 — 카드 위에서만. 300ms·24px 안이면 한 동작으로 본다
            const t = oe.changedTouches && oe.changedTouches[0];
            if (!t || !$(e.target).closest('.ui-cardnews__slide').length) { tapAt = 0; return; }
            const now = Date.now();
            if (now - tapAt < 300 && Math.abs(t.clientX - tapX) < 24 && Math.abs(t.clientY - tapY) < 24) {
                e.preventDefault();              // 뒤따르는 click(바탕 닫기 등) 차단
                tapAt = 0;
                this._sync();
                this.toggleZoom();
                return;
            }
            tapAt = now; tapX = t.clientX; tapY = t.clientY;
        });

        // ── PC ──
        //  그냥 휠은 스크롤(= 읽기)에 맡긴다. 확대는 ctrl/⌘+휠 — 맥 트랙패드 핀치가 이걸로 온다.
        //  이미 확대된 뒤에는 휠이 곧 배율이다(그 상태에서 스크롤은 잠겨 있어 할 일이 없다).
        $el.on('wheel', (e) => {
            const oe = e.originalEvent;
            if (!oe.ctrlKey && !oe.metaKey && this._zoom.scale <= 1) return;
            e.preventDefault();
            this._setZoom(this._zoom.scale * (oe.deltaY > 0 ? 0.9 : 1.1));
            this._commitZoom();
        });
        $el.on('mousedown', '.ui-cardnews__slide img', (e) => {
            if (e.button !== 0 || this._zoom.scale <= 1) return;
            e.preventDefault();
            panning = true;
            grabX = e.clientX - this._zoom.lastX;
            grabY = e.clientY - this._zoom.lastY;
        });
        $(document).on('mousemove.uicardnews', (e) => {
            if (!panning) return;
            this._zoom.x = e.clientX - grabX;
            this._zoom.y = e.clientY - grabY;
            this._applyZoom();
        });
        $(document).on('mouseup.uicardnews', () => {
            if (!panning) return;
            panning = false;
            this._commitZoom();
        });

        $(document).on('keydown.uicardnews', (e) => {
            if (e.key === 'Escape')    this.close();
            if (e.key === 'ArrowDown'  || e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
            if (e.key === 'ArrowUp'    || e.key === 'ArrowLeft')  { e.preventDefault(); this.prev(); }
        });
    }

    /** 스크롤 위치 → 지금 보는 장 → 상단 표시. 확대 중에는 스크롤이 잠겨 있어 불릴 일이 없다. */
    _sync() {
        const $el = this.$el;
        const stage = this._stage();
        if (!$el || !stage) return;

        // 화면 한가운데에 걸친 장이 '지금 보는 장'이다 — 위 끝 기준으로 재면 긴 카드에서 한 장 앞선다
        const mid = stage.getBoundingClientRect().top + stage.clientHeight / 2;
        let idx = 0;
        $el.find('.ui-cardnews__slide').each((i, el) => {
            const r = el.getBoundingClientRect();
            if (r.height > 0 && r.top <= mid) idx = i;   // 아직 안 실린 장(높이 0)은 세지 않는다
        });
        this.index = idx;

        $el.find('.ui-cardnews__count b').text(idx + 1);
        $el.find('.ui-cardnews__bar').each((i, b) => $(b).toggleClass('is-read', i <= idx));

        // 아래로 더 있다는 표시 — 맨 위에서만. 한 번 내리면 할 일을 다 했다
        const more = stage.scrollTop < 20 && stage.scrollHeight - stage.clientHeight > 20;
        $el.find('.ui-cardnews__more').toggleClass('is-on', more);
    }

    /* ───────────────── 확대 ───────────────── */

    /** 배율을 1~5 로 가둔다. 1 로 돌아오면 이동도 원점 — 키우지 않았는데 치우쳐 있으면 안 된다. */
    _setZoom(scale) {
        const z = this._zoom;
        z.scale = Math.min(Math.max(scale, 1), 5);
        if (z.scale <= 1) { z.x = 0; z.y = 0; }
        this._applyZoom();
    }

    /** 손(마우스)을 뗀 시점의 값을 다음 제스처의 출발점으로 삼는다. */
    _commitZoom() {
        const z = this._zoom;
        z.lastScale = z.scale;
        z.lastX = z.x;
        z.lastY = z.y;
    }

    /** 지금 보는 장에만 건다 — 다른 장까지 같이 커지면 스크롤 길이가 튄다. */
    _applyZoom() {
        if (!this.$el) return;
        const z = this._zoom;
        this.$el.find('.ui-cardnews__slide img').each((i, img) => {
            img.style.transform = (i === this.index && z.scale !== 1)
                ? `translate(${z.x}px, ${z.y}px) scale(${z.scale})`
                : '';
        });
        // 확대 중에는 스크롤을 잠근다 (CSS: is-zoomed → overflow hidden, touch-action none)
        this.$el.toggleClass('is-zoomed', z.scale > 1);
    }

    _esc(s) {
        return $('<div>').text(s == null ? '' : s).html();
    }
}
